import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../lib/util';
import {
  MonthFieldRequest,
  PaginatedCompanyCustomBenefitAssignmentEmployeeList
} from '@pxtra-client-lib-legacy/gen';

export const customBenefitAssignmentKeys = {
  all: (benefitId: string) => ['customBenefitAssignments', benefitId] as const,
  list: (benefitId: string, period: MonthFieldRequest) =>
    [...customBenefitAssignmentKeys.all(benefitId), period] as const
};

export function useListCustomBenefitAssignments(
  benefitId: string,
  startAccountingPeriod: MonthFieldRequest,
  options: { enabled?: boolean } = {}
) {
  return useInfiniteQuery({
    queryKey: customBenefitAssignmentKeys.list(benefitId, startAccountingPeriod),
    queryFn: ({ pageParam }) =>
      apiClient.benefits.benefitsCustomEmployeeAssignmentsList({
        benefitId,
        startAccountingPeriodMonth: startAccountingPeriod.month,
        startAccountingPeriodYear: startAccountingPeriod.year,
        offset: pageParam
      }),
    getNextPageParam: (lastPage) => {
      if (!lastPage.next) return undefined;
      const offset = new URL(lastPage.next).searchParams.get('offset');
      return offset ? parseInt(offset) : undefined;
    },
    initialPageParam: 0,
    enabled: !!benefitId && options.enabled !== false
  });
}

export function useCreateCustomBenefitAssignments(benefitId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      startAccountingPeriod,
      amount,
      employees,
      resetToCompanyAmount
    }: {
      startAccountingPeriod: MonthFieldRequest;
      amount: number;
      employees: string[];
      resetToCompanyAmount?: boolean;
    }) => {
      return apiClient.benefits.benefitsCustomEmployeeAssignmentsCreate({
        benefitId,
        requestBody: employees.map((employee) => ({
          start_accounting_period: startAccountingPeriod,
          amount,
          employee,
          reset_to_company_amount: resetToCompanyAmount ?? false
        }))
      });
    },
    onSuccess: (result) => {
      queryClient.setQueriesData<{ pages: PaginatedCompanyCustomBenefitAssignmentEmployeeList[] }>(
        { queryKey: customBenefitAssignmentKeys.all(benefitId) },
        (old) => {
          if (!old) return old;
          const updatedIds = new Set(result.updates.map((u) => u.id));
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              results: page.results.map((employee) =>
                updatedIds.has(employee.id)
                  ? (result.updates.find((u) => u.id === employee.id) ?? employee)
                  : employee
              )
            }))
          };
        }
      );
    }
  });
}

export function useDeleteCustomBenefitAssignments(benefitId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      assignmentIds,
      all = false
    }: {
      assignmentIds: string[];
      all?: boolean;
    }) => {
      return apiClient.benefits.benefitsCustomEmployeeAssignmentsDeleteCreate({
        benefitId,
        requestBody: {
          assignment_ids: assignmentIds,
          all
        }
      });
    },
    onSuccess: (result) => {
      queryClient.setQueriesData<{ pages: PaginatedCompanyCustomBenefitAssignmentEmployeeList[] }>(
        { queryKey: customBenefitAssignmentKeys.all(benefitId) },
        (old) => {
          if (!old) return old;
          const updatedIds = new Set(result.updates.map((u) => u.id));
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              results: page.results.map((employee) =>
                updatedIds.has(employee.id)
                  ? (result.updates.find((u) => u.id === employee.id) ?? employee)
                  : employee
              )
            }))
          };
        }
      );
    }
  });
}
